import { type ReactNode } from 'react';
import { Modal, type ModalProps } from '../Modal/Modal.js';
import { Button } from '../../core/Button/Button.js';
import { Group } from '../../core/Group/Group.js';

export interface DialogProps extends Omit<ModalProps, 'children'> {
  /** Body content. */
  children?: ReactNode;
  /** Confirm action button. If omitted, no confirm button is rendered. */
  onConfirm?: () => void;
  /** Cancel action button. If omitted, no cancel button is rendered. */
  onCancel?: () => void;
  labels?: { confirm?: string; cancel?: string };
  confirmProps?: React.ComponentProps<typeof Button>;
  cancelProps?: React.ComponentProps<typeof Button>;
}

/** Convenience modal with built-in cancel / confirm footer. */
export function Dialog({
  children, onConfirm, onCancel, labels, confirmProps, cancelProps, size = 'sm', ...rest
}: DialogProps): ReactNode {
  return (
    <Modal {...rest} size={size}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--rr-spacing-lg)' }}>
        <div>{children}</div>
        {(onConfirm || onCancel) && (
          <Group justify="flex-end" gap="sm">
            {onCancel ? (
              <Button variant="default" onClick={onCancel} {...cancelProps}>{labels?.cancel ?? 'Cancel'}</Button>
            ) : null}
            {onConfirm ? (
              <Button onClick={onConfirm} {...confirmProps}>{labels?.confirm ?? 'Confirm'}</Button>
            ) : null}
          </Group>
        )}
      </div>
    </Modal>
  );
}
